import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import {
  Plus,
  TrendingUp,
  Users,
  Beaker,
  FileCheck,
  Clock,
  PackageX,
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores por etapa del CRM (las etiquetas vienen del backend: stage_label)
const STAGE_COLORS = {
  contacto_nuevo: 'text-sky-400 bg-sky-500/10',
  interesado: 'text-blue-400 bg-blue-500/10',
  pendiente_instalacion_prueba: 'text-amber-400 bg-amber-500/10',
  prueba_activa: 'text-purple-400 bg-purple-500/10',
  pendiente_contrato: 'text-orange-400 bg-orange-500/10',
  cliente: 'text-emerald-400 bg-emerald-500/10',
  pendiente_retirada: 'text-red-400 bg-red-500/10',
  perdido: 'text-zinc-400 bg-zinc-500/10'
};

// KPI Card Component
function KPICard({ icon: Icon, label, value, trend, color = 'text-white', bgColor = 'bg-white/5' }) {
  return (
    <div className={`p-5 rounded-2xl ${bgColor} border border-white/[0.06] hover:border-white/10 transition-all`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl ${color.replace('text-', 'bg-').replace('400', '500/20')}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {trend !== undefined && (
          <span className={`text-xs font-medium ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-3xl font-light text-white font-['Outfit'] mb-1">{value}</p>
      <p className="text-xs text-zinc-500 font-['Manrope']">{label}</p>
    </div>
  );
}

// Activity Item Component
function ActivityItem({ nombre, stage, stageLabel, date }) {
  const stageColor = STAGE_COLORS[stage] || 'text-zinc-400 bg-zinc-500/10';
  
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);
    
    if (mins < 60) return `Hace ${mins} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days < 7) return `Hace ${days}d`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-white/[0.04] last:border-0">
      <div className="flex items-center gap-3">
        <Building2 className="w-4 h-4 text-zinc-500" />
        <div>
          <p className="text-sm text-white font-['Manrope']">{nombre}</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${stageColor}`}>
            {stageLabel}
          </span>
        </div>
      </div>
      <span className="text-xs text-zinc-500 font-['Manrope']">{formatDate(date)}</span>
    </div>
  );
}

// Pipeline Summary Card
function PipelineSummary({ data }) {
  const stages = [
    { key: 'contacto_nuevo', label: 'Leads', icon: Users },
    { key: 'pendiente_instalacion_prueba', label: 'Por Instalar', icon: Clock },
    { key: 'prueba_activa', label: 'En Prueba', icon: Beaker },
    { key: 'pendiente_contrato', label: 'Por Cerrar', icon: FileCheck },
    { key: 'cliente', label: 'Convertidos', icon: CheckCircle },
  ];

  return (
    <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
      <h3 className="text-sm text-zinc-500 mb-4 font-['Manrope'] uppercase tracking-wider">Pipeline Resumen</h3>
      <div className="grid grid-cols-5 gap-3">
        {stages.map((stage) => {
          const Icon = stage.icon;
          const count = data?.[stage.key] || 0;
          const color = STAGE_COLORS[stage.key];
          return (
            <div key={stage.key} className="text-center">
              <div className={`mx-auto w-10 h-10 rounded-xl flex items-center justify-center ${color.split(' ')[1]}`}>
                <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
              </div>
              <p className="text-xl font-light text-white mt-2 font-['Outfit']">{count}</p>
              <p className="text-[10px] text-zinc-500 font-['Manrope']">{stage.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ComercialDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/comercial/stats`, { 
        withCredentials: true 
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching commercial stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <img src="/manklar-logo-white.png" alt="Manklar" className="h-10 opacity-50" />
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const kpis = stats?.kpis || {};
  const crmSummary = stats?.crm_resumen || {};
  const activity = stats?.actividad_reciente || [];

  return (
    <ComercialLayout
      title="Panel Comercial"
      subtitle={`Bienvenido, ${user?.name || 'Comercial'}`}
      stats={kpis}
      onLogout={handleLogout}
      headerActions={
        <Link to="/comercial/nuevo-contacto">
          <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl shadow-lg shadow-emerald-500/20" data-testid="panel-nuevo-contacto-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Contacto
          </Button>
        </Link>
      }
    >
      {/* Main CTA */}
      <Link to="/comercial/nueva-prueba">
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 hover:border-emerald-500/50 transition-all cursor-pointer group">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/20">
                <Plus className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-lg font-medium text-white font-['Outfit']">Registrar Nueva Prueba Gratuita</h2>
                <p className="text-sm text-zinc-400 font-['Manrope']">Añade un nuevo cliente potencial en menos de 1 minuto</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>

      {/* KPIs Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard 
          icon={Beaker} 
          label="Pruebas hoy" 
          value={kpis.pruebas_hoy || 0}
          color="text-sky-400"
          bgColor="bg-sky-500/[0.08]"
        />
        <KPICard 
          icon={Calendar} 
          label="Pruebas este mes" 
          value={kpis.pruebas_mes || 0}
          color="text-purple-400"
          bgColor="bg-purple-500/[0.08]"
        />
        <KPICard 
          icon={CheckCircle} 
          label="Clientes convertidos" 
          value={kpis.clientes || 0}
          color="text-emerald-400"
          bgColor="bg-emerald-500/[0.08]"
        />
        <KPICard 
          icon={TrendingUp} 
          label="Ratio conversión" 
          value={`${kpis.ratio_conversion || 0}%`}
          color="text-amber-400"
          bgColor="bg-amber-500/[0.08]"
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="p-4 rounded-xl bg-amber-500/[0.06] border border-amber-500/20">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-400" />
            <div>
              <p className="text-2xl font-light text-white font-['Outfit']">{kpis.pendientes_instalacion || 0}</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Pendientes instalación</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-orange-500/[0.06] border border-orange-500/20">
          <div className="flex items-center gap-3">
            <FileCheck className="w-5 h-5 text-orange-400" />
            <div>
              <p className="text-2xl font-light text-white font-['Outfit']">{kpis.pendientes_contrato || 0}</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Pendientes contrato</p>
            </div>
          </div>
        </div>
        <div className="p-4 rounded-xl bg-red-500/[0.06] border border-red-500/20">
          <div className="flex items-center gap-3">
            <PackageX className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-2xl font-light text-white font-['Outfit']">{kpis.pendientes_retirada || 0}</p>
              <p className="text-xs text-zinc-500 font-['Manrope']">Pendientes retirada</p>
            </div>
          </div>
        </div>
      </div>

      {/* CRM Summary + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <PipelineSummary data={crmSummary} />
          
          {/* Quick Actions */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link to="/comercial/crm">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all text-center">
                <Beaker className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                <p className="text-xs text-zinc-400 font-['Manrope']">Ver CRM</p>
              </div>
            </Link>
            <Link to="/comercial/pruebas">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all text-center">
                <Users className="w-5 h-5 text-sky-400 mx-auto mb-2" />
                <p className="text-xs text-zinc-400 font-['Manrope']">Pruebas Activas</p>
              </div>
            </Link>
            <Link to="/comercial/agenda">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all text-center">
                <Calendar className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                <p className="text-xs text-zinc-400 font-['Manrope']">Agenda</p>
              </div>
            </Link>
            <Link to="/comercial/objetivos">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/10 transition-all text-center">
                <AlertCircle className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                <p className="text-xs text-zinc-400 font-['Manrope']">Objetivos</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <h3 className="text-sm text-zinc-500 mb-4 font-['Manrope'] uppercase tracking-wider">Actividad Reciente</h3>
          {activity.length > 0 ? (
            <div className="space-y-0">
              {activity.slice(0, 8).map((item) => (
                <ActivityItem 
                  key={item.id}
                  nombre={item.display_name || item.empresa || item.nombre_contacto}
                  stage={item.crm_stage}
                  stageLabel={item.stage_label}
                  date={item.updated_at || item.created_at}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-sm text-zinc-500 font-['Manrope']">Sin actividad reciente</p>
              <Link to="/comercial/nueva-prueba">
                <Button variant="link" className="text-emerald-400 mt-2">
                  Crear primera prueba
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </ComercialLayout>
  );
}
